import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { AuthState, AuthUser } from '../types/auth';
import * as authService from '../services/authService';
import * as cognitoClient from '../lib/cognitoClient';
import { clearAccountDeleted, isAccountDeleted, subscribeAccountDeleted } from '../lib/accountDeleted';
import { clearAccountApproval, getAccountApproval, subscribeAccountApproval } from '../lib/accountApproval';
import { clearSessionExpired, isSessionExpired, subscribeSessionExpired } from '../lib/sessionExpired';
import {
  TOKENS_KEY,
  clearStoredTokens,
  computeExpiresAt,
  decodeJwtClaims,
  readStoredTokens,
  subscribeStoredTokens,
  writeStoredTokens,
  type StoredTokens,
} from '../lib/authTokens';
import { refreshSession } from '../lib/sessionRefresh';
import { setSessionRefresher } from '../lib/apiClient';
import { queryClient as appQueryClient } from '../lib/queryClient';
import { clearUserClientState, decodeJwtSub, ensureCacheOwner } from '../lib/userSession';
import { clearVoiceQueue } from '../services/voiceQueue';
import {
  IDLE_SIGNOUT_KEY,
  broadcastIdleSignOut,
  clearIdleSignedOut,
  isIdleExpired,
  isIdleSignedOut,
  markIdleSignedOut,
  recordActivity,
  resetActivityTracking,
  subscribeIdleSignedOut,
} from '../lib/idleSession';

export type { AuthUser };
export type { LoginResult } from '../services/authService';

export interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  privacyPolicyVersion: string;
  tosVersion: string;
  /** Optional: "Produkt-Updates und Release Notes per E-Mail erhalten". */
  productUpdates?: boolean;
  /** Optional: "Erinnerungen an fällige Aufgaben per E-Mail erhalten". */
  taskReminders?: boolean;
  /** UI language — the language of the e-mails. */
  locale?: string;
}

// ── Context shape ──────────────────────────────────────────────────────────

interface AuthContextValue {
  state: AuthState;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<authService.LoginResult>;
  /** Second sign-in step: the code from the authenticator app for a login's MFA challenge. */
  confirmMfa: (
    email: string,
    challenge: authService.MfaChallenge,
    code: string,
  ) => Promise<authService.MfaConfirmResult>;
  logout: () => Promise<void>;
  register: (input: RegisterInput) => Promise<cognitoClient.RegisterResult>;
  /** Confirms the sign-up code; answers the account's approval state (closed beta). */
  verifyOtp: (email: string, code: string) => Promise<cognitoClient.VerifyOtpResult>;
  resendOtp: (email: string) => Promise<void>;
}

export type { AuthContextValue };
export const AuthContext = createContext<AuthContextValue | null>(null);

// ── Token persistence (lib/authTokens) ─────────────────────────────────────

/** Refresh this long before the access token expires (while the tab is visible). */
export const PROACTIVE_REFRESH_MARGIN_MS = 2 * 60 * 1000;
/** setTimeout's maximum delay (~24.8 days). */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * The stored session, if usable. An expired access token is kept when there
 * is a refresh token — the session is renewed right after mount.
 */
function loadPersistedTokens(): StoredTokens | null {
  const stored = readStoredTokens();
  if (!stored || !stored.idToken) return null;
  if (stored.expiresAt && stored.expiresAt < Date.now() && stored.refreshToken === '') {
    clearStoredTokens();
    return null;
  }
  return stored;
}

function decodeJwtEmail(idToken: string, fallback: string): string {
  const email = decodeJwtClaims(idToken)['email'];
  return typeof email === 'string' ? email : fallback;
}

function decodeJwtGroups(idToken: string): string[] {
  const groups = decodeJwtClaims(idToken)['cognito:groups'];
  return Array.isArray(groups) ? (groups as unknown[]).filter((g): g is string => typeof g === 'string') : [];
}

function signedOutState(): AuthState {
  return { isAuthenticated: false, isLoading: false, user: null, tokens: null };
}

// ── Provider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  // The React Query cache holds the signed-in user's data; it is wiped on
  // logout, account deletion and account switch (lib/userSession).
  const contextQueryClient = useContext(QueryClientContext);
  const queryClient = contextQueryClient ?? appQueryClient;

  const [state, setState] = useState<AuthState>(() => {
    // Restore session from localStorage on first render
    const persisted = loadPersistedTokens();
    // Last activity longer ago than the idle timeout (closed tab, laptop sleep):
    // never restore the session — the idle sign-out below revokes and clears it.
    if (persisted && isIdleExpired()) {
      markIdleSignedOut();
      return signedOutState();
    }
    if (persisted) {
      // Client state left by a different user on this device must not be shown.
      ensureCacheOwner(decodeJwtSub(persisted.idToken), queryClient);
      const email = decodeJwtEmail(persisted.idToken, '');
      const groups = decodeJwtGroups(persisted.idToken);
      return {
        isAuthenticated: true,
        isLoading: false,
        user: { email, userId: '', groups, idToken: persisted.idToken },
        tokens: persisted,
      };
    }
    return signedOutState();
  });

  // apiClient renews the session through this on a 401 (single-flight).
  useEffect(() => setSessionRefresher(refreshSession), []);

  // Resolve the loading flag after the initial mount.
  useEffect(() => {
    setState(prev => ({ ...prev, isLoading: false }));
  }, []);

  // The API answered 401 ACCOUNT_DELETED: the account no longer exists, so
  // sign out locally (no Cognito call — the user is gone there too).
  // ProtectedRoute then redirects to the login page, which shows the notice.
  useEffect(() => {
    const signOutDeleted = (): void => {
      if (!isAccountDeleted()) return;
      clearStoredTokens();
      clearUserClientState(queryClient);
      setState(signedOutState());
    };
    signOutDeleted();
    return subscribeAccountDeleted(signOutDeleted);
  }, [queryClient]);

  // Closed beta: the API answered 403 ACCOUNT_PENDING_APPROVAL / ACCOUNT_REJECTED.
  // The account must not use the app: revoke the refresh token (best effort),
  // drop the session and the cached data. ProtectedRoute then leads to
  // /account-pending — a public page that calls no API, so there is no loop.
  useEffect(() => {
    const signOutUnapproved = (): void => {
      if (getAccountApproval() === null) return;
      const stored = readStoredTokens();
      if (stored !== null) {
        clearStoredTokens();
        clearUserClientState(queryClient);
        if (stored.refreshToken) void authService.logout(stored.refreshToken);
      }
      setState(prev => (prev.isAuthenticated ? signedOutState() : prev));
    };
    signOutUnapproved();
    return subscribeAccountApproval(signOutUnapproved);
  }, [queryClient]);

  // The session could not be renewed (apiClient: 401 + failed silent
  // refresh): sign out; the login page says "Your session has expired".
  useEffect(() => {
    const signOutExpired = (): void => {
      if (!isSessionExpired()) return;
      clearStoredTokens();
      clearUserClientState(queryClient);
      setState(signedOutState());
    };
    signOutExpired();
    return subscribeSessionExpired(signOutExpired);
  }, [queryClient]);

  // Signed out for inactivity (lib/idleSession: IdleSessionGuard, the stale
  // check on start, sessionRefresh, or another tab): revoke the refresh token
  // (best effort), drop the session and the cached data, and tell the other
  // tabs. ProtectedRoute then leads to /login?redirect=…, which shows the notice.
  useEffect(() => {
    const signOutIdle = (): void => {
      if (!isIdleSignedOut()) return;
      const stored = readStoredTokens();
      if (stored !== null) {
        broadcastIdleSignOut();
        clearStoredTokens();
        if (stored.refreshToken) void authService.logout(stored.refreshToken);
      }
      resetActivityTracking();
      clearUserClientState(queryClient);
      setState(prev => (prev.isAuthenticated ? signedOutState() : prev));
    };
    signOutIdle();
    return subscribeIdleSignedOut(signOutIdle);
  }, [queryClient]);

  // Another tab signed out (manually, for inactivity, or its session ended):
  // the tokens are gone from the shared storage, so sign this tab out too.
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === IDLE_SIGNOUT_KEY && event.newValue !== null) {
        markIdleSignedOut();
        return;
      }
      if ((event.key === TOKENS_KEY || event.key === null) && readStoredTokens() === null) {
        clearUserClientState(queryClient);
        setState(prev => (prev.isAuthenticated ? signedOutState() : prev));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [queryClient]);

  // A silent refresh stored new tokens: keep the context in sync (new ID
  // token = current name/groups, new expiry = next proactive refresh).
  useEffect(
    () =>
      subscribeStoredTokens((tokens) => {
        if (!tokens || !tokens.idToken) return;
        setState(prev => {
          if (!prev.isAuthenticated) return prev;
          return {
            ...prev,
            user: prev.user
              ? { ...prev.user, idToken: tokens.idToken, groups: decodeJwtGroups(tokens.idToken), email: decodeJwtEmail(tokens.idToken, prev.user.email) }
              : prev.user,
            tokens,
          };
        });
      }),
    [],
  );

  // Proactive refresh shortly before the access token expires — only while
  // the tab is visible (a hidden tab refreshes when it becomes visible again,
  // or on its next 401). Failures are left to the 401 path in apiClient.
  const expiresAt = state.tokens?.expiresAt ?? 0;
  const canRefresh = state.isAuthenticated && (state.tokens?.refreshToken ?? '') !== '';
  useEffect(() => {
    if (!canRefresh || !expiresAt) return undefined;
    const refreshIfDue = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (expiresAt - Date.now() <= PROACTIVE_REFRESH_MARGIN_MS) void refreshSession();
    };
    const delay = Math.min(Math.max(0, expiresAt - PROACTIVE_REFRESH_MARGIN_MS - Date.now()), MAX_TIMER_MS);
    const timer = setTimeout(refreshIfDue, delay);
    document.addEventListener('visibilitychange', refreshIfDue);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', refreshIfDue);
    };
  }, [canRefresh, expiresAt]);

  /** Stores the tokens of a completed sign-in (password, or password + MFA code). */
  const startSession = useCallback(
    (result: { tokens: authService.AuthTokens; user: { email: string; userId: string } }): void => {
      clearAccountDeleted();
      clearSessionExpired();
      clearAccountApproval();
      clearIdleSignedOut();
      const user: AuthUser = {
        email: result.user.email,
        userId: result.user.userId,
        groups: decodeJwtGroups(result.tokens.idToken),
        idToken: result.tokens.idToken,
      };
      const tokens: StoredTokens = {
        accessToken: result.tokens.accessToken,
        idToken: result.tokens.idToken,
        refreshToken: result.tokens.refreshToken ?? '',
        expiresAt: computeExpiresAt(result.tokens.accessToken),
      };
      writeStoredTokens(tokens);
      // A different account (e.g. re-registered after deletion) must never
      // see the previous account's cached data or selections.
      ensureCacheOwner(user.userId || decodeJwtSub(tokens.idToken), queryClient);
      // Signing in is activity: the shared idle clock starts now (after
      // ensureCacheOwner, which may wipe the previous user's storage).
      recordActivity({ force: true });
      setState({
        isAuthenticated: true,
        isLoading: false,
        user,
        tokens,
      });
    },
    [queryClient],
  );

  const confirmMfa = useCallback(
    async (
      email: string,
      challenge: authService.MfaChallenge,
      code: string,
    ): Promise<authService.MfaConfirmResult> => {
      const result = await authService.mfaConfirm(email, challenge, code);
      if (result.type === 'success') startSession(result);
      return result;
    },
    [startSession],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<authService.LoginResult> => {
      const result = await authService.login(email, password);

      if (result.type === 'success') startSession(result);

      return result;
    },
    [startSession],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      // Revokes the refresh token server-side (best effort).
      await authService.logout(readStoredTokens()?.refreshToken || undefined);
    } catch {
      // Logout is best-effort — clear local state regardless
    }
    clearStoredTokens();
    clearUserClientState(queryClient);
    // Voice notes waiting for upload on this device (privacy review D16).
    void clearVoiceQueue();
    setState(signedOutState());
  }, [queryClient]);

  const register = useCallback(
    async (input: RegisterInput): Promise<cognitoClient.RegisterResult> => {
      const emailOptions: cognitoClient.RegisterEmailOptions = {
        ...(input.productUpdates !== undefined ? { productUpdates: input.productUpdates } : {}),
        ...(input.taskReminders !== undefined ? { taskReminders: input.taskReminders } : {}),
        ...(input.locale !== undefined ? { locale: input.locale } : {}),
      };
      const args = [
        input.email,
        input.password,
        input.firstName,
        input.lastName,
        input.privacyPolicyVersion,
        input.tosVersion,
      ] as const;
      return Object.keys(emailOptions).length > 0
        ? cognitoClient.register(...args, emailOptions)
        : cognitoClient.register(...args);
    },
    [],
  );

  const verifyOtp = useCallback(
    async (email: string, code: string): Promise<cognitoClient.VerifyOtpResult> =>
      cognitoClient.verifyOtp(email, code),
    [],
  );

  const resendOtp = useCallback(
    async (email: string): Promise<void> => {
      await cognitoClient.resendOtp(email);
    },
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      user: state.user,
      login,
      confirmMfa,
      logout,
      register,
      verifyOtp,
      resendOtp,
    }),
    [state, login, confirmMfa, logout, register, verifyOtp, resendOtp],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Consumer hook ──────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
